/**
 * SummarySection Component
 * 입출금 합계 표시 섹션
 */
import React, { useMemo } from 'react';
import { HeadWithdrawHistory, HeadGetWithdrawableAmountResponseDto } from '../types/headWithdraw.types';
import { formatAmount } from '../utils/amountFormatter';
import { getItem } from '@/shared/utils/storage';

interface SummarySectionProps {
    /** 전체 입출금 내역 목록 */
    histories: HeadWithdrawHistory[];
    /** 예치금 잔액 */
    withdrawableAmount: HeadGetWithdrawableAmountResponseDto | null;
}

const SummarySection: React.FC<SummarySectionProps> = ({ histories, withdrawableAmount }) => {
    /** 합계 계산 */
    const summary = useMemo(() => {
        let depositTotal = 0; // 입금 합계
        let withdrawalTotal = 0; // 출금 합계
        let feeTotal = 0; // 수수료 합계

        histories.forEach((history) => {
            // 라이더(reqType='R')와 협력사(reqType='S')의 계산 로직
            if (history.reqType === 'R') {
                // 라이더 입금: RP, RW, TI, T, D, RR
                if (['RP', 'RW', 'TI', 'T', 'D', 'RR'].includes(history.acctType)) {
                    depositTotal += history.amount;
                }
                // 라이더 출금: W, F, TO
                else if (['W', 'F', 'TO'].includes(history.acctType)) {
                    withdrawalTotal += history.amount;
                }
            } else if (history.reqType === 'S') {
                // 협력사 입금: SW, T, TI, SR, D
                if (['SW', 'T', 'TI', 'SR', 'D'].includes(history.acctType)) {
                    depositTotal += history.amount;
                }
                // 협력사 출금: W, F, TO
                else if (['W', 'F', 'TO'].includes(history.acctType)) {
                    withdrawalTotal += history.amount;
                }
            }
            
            // 수수료 합계: F(수수료) 타입의 tranAmt(amount) 합산
            if (history.acctType === 'F') {
                feeTotal += history.amount;
            }
        });

        const netTotal = depositTotal + withdrawalTotal; // 순합계 (입금 + 출금)

        return {
            depositTotal,
            withdrawalTotal,
            feeTotal,
            netTotal
        };
    }, [histories]);

    const balanceAmount = withdrawableAmount?.balAmt || 0;

    // 로컬스토리지에서 ADMIN_YN 값 확인
    const adminYn = getItem<string | boolean>('ADMIN_YN');
    const isAdminYn = adminYn === true || adminYn === 'true' || adminYn === 'Y';

    return (
        <div className="card">
            <div className="card-body">
                <div className="summary-container">
                    {isAdminYn && (
                        <div className="summary-item">
                            <span className="summary-label">예치금 잔액</span>
                            <span className="summary-amount">
                                {formatAmount(balanceAmount)}
                            </span>
                        </div>
                    )}
                    <div className="summary-item">
                        <span className="summary-label">입금 합계</span>
                        <span className="summary-amount amount-deposit">
                            {formatAmount(summary.depositTotal)}
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">출금 합계</span>
                        <span className="summary-amount amount-withdrawal">
                            {formatAmount(summary.withdrawalTotal)}
                        </span>
                    </div>
                    <div className="summary-item summary-total">
                        <span className="summary-label">순합계</span>
                        <span className={`summary-amount ${summary.netTotal >= 0 ? 'amount-deposit' : 'amount-withdrawal'}`}>
                            {summary.netTotal >= 0 ? '+' : ''}{formatAmount(summary.netTotal)}
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">수수료 합계</span>
                        <span className="summary-amount">
                            {formatAmount(summary.feeTotal)}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SummarySection;
